import React, { useState, useMemo } from 'react';
import { Loader2, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getRoleService } from '@/services/roleService';
import { useDataSource } from '@/contexts/DataSourceContext';
import type { CreateRoleRateDto } from '@/types/role';
import { toast } from 'sonner';

interface AddRateDialogProps {
  roleId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AddRateDialog: React.FC<AddRateDialogProps> = ({
  roleId,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('roles-rates');
  const roleService = useMemo(() => getRoleService(screenMode), [screenMode]);
  
  const [billingRate, setBillingRate] = useState('');
  const [costRate, setCostRate] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [isSaving, setIsSaving] = useState(false);

  const calculateMargin = () => {
    if (!billingRate || !costRate) return null;
    const billing = parseFloat(billingRate);
    const cost = parseFloat(costRate);
    if (billing === 0) return null;
    return ((billing - cost) / billing) * 100;
  };

  const margin = calculateMargin();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!billingRate && !costRate) {
      toast.error('Please enter at least one rate');
      return;
    }

    setIsSaving(true);

    try {
      const dto: CreateRoleRateDto = {
        billingRate: billingRate ? parseFloat(billingRate) : undefined,
        costRate: costRate ? parseFloat(costRate) : undefined,
        currency,
        effectiveFrom,
      };

      await roleService.createRate(roleId, dto);
      
      // Reset form
      setBillingRate('');
      setCostRate('');
      setEffectiveFrom(new Date().toISOString().split('T')[0]);
      
      onSuccess();
      toast.success('Rate added successfully');
    } catch (err) {
      console.error('Error creating rate:', err);
      toast.error('Failed to add rate');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add New Rate</DialogTitle>
            <DialogDescription>
              Set a new billing and/or cost rate for this role
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                This rate will become effective from the date you specify. Historical rates are preserved for accurate reporting.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-billingRate">Billing Rate ($/hr)</Label>
                <Input
                  id="add-billingRate"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="150.00"
                  value={billingRate}
                  onChange={(e) => setBillingRate(e.target.value)}
                />
                {billingRate && (
                  <div className="text-xs text-muted-foreground">
                    ${(parseFloat(billingRate) * 8).toFixed(2)}/day
                    {' • '}
                    ${(parseFloat(billingRate) * 160).toFixed(2)}/month
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-costRate">Cost Rate ($/hr)</Label>
                <Input
                  id="add-costRate"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="85.00"
                  value={costRate}
                  onChange={(e) => setCostRate(e.target.value)}
                />
                {costRate && (
                  <div className="text-xs text-muted-foreground">
                    ${(parseFloat(costRate) * 8).toFixed(2)}/day
                    {' • '}
                    ${(parseFloat(costRate) * 160).toFixed(2)}/month
                  </div>
                )}
              </div>
            </div>

            {margin !== null && (
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-sm font-medium">Margin</div>
                <div className={`text-2xl font-bold ${margin > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {margin.toFixed(1)}%
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="add-effectiveFrom">Effective From</Label>
              <Input
                id="add-effectiveFrom"
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                The date from which this rate becomes active
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Rate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
